import React, { useCallback, useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import * as authStore from '../../r/authStore';
import * as recordStore from '../../r/recordStore';
import { useSelector, useDispatch } from 'react-redux';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarBorder';
import Slide from '@mui/material/Slide';
import * as constants from '../../r/constants';
import { SearchBoxHeader } from '../helpers/SearchBoxHeader';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export const ReactorSearchDialog = ({ isOpen, setIsOpen }) => {

    const dispatch = useDispatch();

    const minSearchTextLength = 2;

    const [searchText, setSearchText] = useState('');
    const [searchResultsState, setSearchResultsState] = useState([]);

    const tnsUserId = useSelector(state => authStore.selectors.selectTnsUserId(state));
    const allReactors = useSelector(state => recordStore.selectors.selectRecords(state, constants.rnryRecordTypes.rnryReactorExpandedDto));
    const userReactors = useSelector(state => recordStore.selectors.selectRecords(state, constants.rnryRecordTypes.rnryUserReactor));
    const addUserReactor = useCallback(record => dispatch(recordStore.actionCreators.createRecord(constants.rnryRecordTypes.rnryUserReactor, record)), [dispatch]);
    const deleteUserReactor = useCallback(record => dispatch(recordStore.actionCreators.deleteRecord(constants.rnryRecordTypes.rnryUserReactor, record)), [dispatch]);

    useEffect(() => {
        if (isOpen) {
            if (searchText && searchText.length > 0) {
                const filtered = allReactors.filter(x => x.reactorNamePatreon.toLowerCase().includes(searchText.toLowerCase()));
                setSearchResultsState(filtered);
            } else {
                setSearchResultsState(allReactors);
            }
        }
    }, [allReactors, isOpen, searchText]);

    const removeFromLibrary = useCallback(reactor => {
        const userRecord = userReactors.find(ur => ur.reactorRnryUserId === reactor.rnryUserRecord.id);
        deleteUserReactor(userRecord);
    }, [deleteUserReactor, userReactors]);

    const addToLibrary = useCallback(reactor => {
        const userRecord = {
            tnsUserId: tnsUserId,
            reactorRnryUserId: reactor.rnryUserRecord.id
        };
        addUserReactor(userRecord);
    }, [addUserReactor, tnsUserId]);

    const getLibraryIcon = reactor => {
        const isIn = userReactors.some(ur => ur.reactorRnryUserId === reactor.rnryUserRecord.id);
        const starIcon = isIn ? <StarIcon sx={classes.bookmarkIcon} /> : <StarOutlineIcon sx={{ ...classes.bookmarkIcon, color: 'rgb(255,255,255,0.5)' }} />;
        const icon = (
            <Box sx={classes.bookmarkIconBox}>
                <IconButton disableRipple onClick={isIn ? () => removeFromLibrary(reactor) : () => addToLibrary(reactor)}>
                    {starIcon}
                </IconButton>
            </Box>
        );
        return icon;
    };

    const handleClose = () => {
        setSearchText('');
        setSearchResultsState([]);
        //resetSearch();
        setIsOpen(false);
    };

    const handleKeyDown = e => {
        if (searchText.trim().length >= 2 && e.key === 'Enter') {
            //searchMovies();
        }
    }

    const getImageUrl = reactor => {
        if (reactor.avatarUrlPatreon && reactor.avatarUrlPatreon !== '') {
            return reactor.avatarUrlPatreon;
        } else {
            return '/i/no-image.jpg';
        }
    }

    const classes = {
        posterBoxList: {
            flexGrow: 1,
            p: 3,
            display: 'flex',
            flexWrap: 'wrap',
            alignContent: 'flex-start',
            backgroundColor: 'grey.800'
        },
        posterBoxBorder: {
            border: 2,
            borderColor: 'grey.800',
            borderRadius: '10px',
            margin: '12px',
            backgroundColor: 'grey.900',
            boxShadow: '3px 3px 5px -1px rgba(0,0,0,0.4), 5px 5px 8px 0px rgba(0,0,0,0.28), 1px 1px 14px 0px rgba(0,0,0,0.24)',
            mozUserSelect: 'none',
            msUserSelect: 'none',
            webkitUserSelect: 'none',
            userSelect: 'none',
            transform: 'translate(0%, 0%)',
            transition: 'transform .5s ease-in-out, box-shadow .5s ease-in-out, background-color .5s ease-in-out',
            webkitTransition: 'transform .5s ease-in-out, box-shadow .5s ease-in-out, background-color .5s ease-in-out',
            mozTransition: 'transform .5s ease-in-out, box-shadow .5s ease-in-out, background-color .5s ease-in-out',
            oTransition: 'transform .5s ease-in-out, box-shadow .5s ease-in-out, background-color .5s ease-in-out',
            '&:hover': {
                //cursor: 'pointer',
                transform: 'translate(-2%, -2%)',
                transition: 'transform .1s ease-in-out, box-shadow .1s ease-in-out, background-color .1s ease-in-out',
                webkitTransition: 'transform .1s ease-in-out, box-shadow .1s ease-in-out, background-color .1s ease-in-out',
                mozTransition: 'transform .1s ease-in-out, box-shadow .1s ease-in-out, background-color .1s ease-in-out',
                oTransition: 'transform .1s ease-in-out, box-shadow .1s ease-in-out, background-color .1s ease-in-out',
            },
            '&:active': {
                //cursor: 'pointer',
                transform: 'translate(-1%, -1%)',
                transition: 'transform .1s ease-in-out, box-shadow .1s ease-in-out, background-color .1s ease-in-out',
                webkitTransition: 'transform .1s ease-in-out, box-shadow .1s ease-in-out, background-color .1s ease-in-out',
                mozTransition: 'transform .1s ease-in-out, box-shadow .1s ease-in-out, background-color .1s ease-in-out',
                oTransition: 'transform .1s ease-in-out, box-shadow .1s ease-in-out, background-color .1s ease-in-out',
            }
        },
        posterBox: {
            display: 'flex',
            flexDirection: 'column',
            width: '200px',
            height: '356px',
            margin: 2
        },
        posterBoxTitle: {
            marginTop: 2,
            fontSize: 'body2.fontSize',
            color: 'common.white',
            textOverflow: 'ellipsis',
            textWrap: 'nowrap',
            overflow: 'hidden'
        },
        posterBoxYear: {
            marginTop: 0,
            fontSize: 'body2.fontSize',
            color: 'grey.500',
            textOverflow: 'ellipsis',
            textWrap: 'nowrap',
            overflow: 'hidden'
        },
        bookmarkIcon: {
            height: '32px',
            width: '32px'
        },
        bookmarkIconBox: {
            width: '40px',
            height: '40px',
            backgroundColor: '#831c1c',
            color: 'white',
            position: 'absolute',
            right: 0,
            top: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cornerRadius: '0 10px 0 10px'
        }
    }

    return (
        <Dialog
            fullScreen
            disableRestoreFocus
            open={isOpen}
            onClose={handleClose}
            TransitionComponent={Transition}
        >
            <AppBar sx={{ position: 'relative' }}>
                <Toolbar>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={handleClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                        {'Add Reactors'}
                    </Typography>
                </Toolbar>
            </AppBar>
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, m: 3 }} >
                <SearchBoxHeader sx={{ alignSelf: 'center', minWidth: '25%' }} searchText={searchText} setSearchText={setSearchText} minLength={minSearchTextLength} />
                <Box sx={classes.posterBoxList}>
                    {searchResultsState.map(reactor => (
                        <Box sx={classes.posterBoxBorder} key={reactor.rnryUserRecord.id}>
                            <Box sx={classes.posterBox}>
                                <img width={200} height={300} src={getImageUrl(reactor)} alt={reactor.reactorNamePatreon} loading={'lazy'} />
                                <Tooltip title={reactor.reactorNamePatreon}>
                                    <Typography sx={classes.posterBoxTitle}>{reactor.reactorNamePatreon}</Typography>
                                </Tooltip>
                                <Typography sx={classes.posterBoxYear}>{reactor.homePageUrlPatreon}</Typography>
                            </Box>
                            {getLibraryIcon(reactor)}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Dialog>
    );
};